<template>
    <v-container fluid class="my-results">
        <v-card class="my-results__card elevation-3" :loading="loading">
            <div class="card-title my-results__title">
                <span class="my-results__title-main">
                    <v-icon class="my-results__title-icon" size="20">mdi-poll</v-icon>
                    <span class="my-results__title-text">{{ $t('SurveyResults') }}</span>
                </span>
                <router-link :to="{ name: 'my-submissions'}" custom>
                    <v-btn fab x-small class="white--text my-results__back" color="secondary" role="link" :title="$t('Back')" :aria-label="$t('Back')">
                        <v-icon>mdi-arrow-left-circle</v-icon>
                    </v-btn>
                </router-link>
            </div>

            <v-card-text class="my-results__body">
                <v-fade-transition mode="out-in">
                    <!-- first load: a placeholder shaped like the grid that is coming -->
                    <GridSkeleton v-if="showSkeleton"
                                  key="loading"
                                  :rows="6"
                                  :columns="5"
                                  :label="tr('LoadingResults', 'جارٍ تحميل النتائج…', 'Loading results…')" />

                    <!-- results -->
                    <div v-else-if="results.length > 0" key="results">
                        <!-- Survey identity strip: ties the green page header to the table
                             below it instead of leaving a bare heading floating above. -->
                        <div class="my-results__toolbar">
                            <span class="my-results__toolbar-accent" aria-hidden="true" />
                            <h2 class="my-results__heading">{{ title }}</h2>
                            <span class="my-results__count">{{ totalCount }} {{ tr('Results', 'النتائج', 'Results') }}</span>
                        </div>
                        <GridWithServerPaging :headers="headers"
                                              :items="results"
                                              :total="totalCount"
                                              :loading="loading"
                                              disable-sort
                                              class="mt-4 my-results__table"
                                              @onChange="listSurveyresults">
                            <template v-slot:[`item.user`]="{ item }">
                                <b class="primary--text">{{ item.user }}</b>
                            </template>
                            <!-- Question labels are authored in the rich text editor, so they
                                 arrive as markup. Rendered per column through a sanitized
                                 string; a plain {{ }} binding printed the tags verbatim. -->
                            <template v-for="column in questionHeaders" v-slot:[`header.${column.value}`]="{ header }">
                                <!-- title carries the plain-text question, so a header that
                                     still wraps to several lines can be read on hover -->
                                <span :key="column.value" class="my-results__q" :title="header.text" v-html="header.html"></span>
                            </template>
                        </GridWithServerPaging>
                    </div>

                    <!-- empty: only once the request has actually come back -->
                    <div v-else-if="hasLoaded" key="empty" class="my-results__empty">
                        <span class="my-results__empty-icon">
                            <v-icon x-large>mdi-clipboard-text-outline</v-icon>
                        </span>
                        <p class="my-results__empty-title">{{ $t('NoItemsToShow') }}</p>
                        <p class="my-results__empty-hint">
                            {{ tr('NoResultsHint', 'لا توجد نتائج لعرضها لهذه المشاركة حتى الآن.', 'There are no results to show for this participation yet.') }}
                        </p>
                    </div>
                </v-fade-transition>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
    import SurveyService from '@/services/SurveyService'
    import GridWithServerPaging from '@/components/controls/GridWithServerPaging.vue'
    import GridSkeleton from '@/components/controls/GridSkeleton.vue'
    import { useSanitizeHtml, useStripHtml } from '@/helpers/common.js'


    export default {
        components: {
            GridWithServerPaging, GridSkeleton
        },

        data() {
            return {
                loading: false,
                // false until the first request settles, so the empty state cannot be
                // rendered while the results are still on their way
                hasLoaded: false,
                surveyResult: null,
                options: {
                    page: 1,
                    itemsPerPage: 20
                },
                totalCount: 10,
                headers: [],
                results: [],
                title: ''
            }
        },

        computed: {
            // full placeholder on the first load only; paging afterwards keeps the grid
            // on screen and uses its own progress bar
            showSkeleton() {
                return this.loading && !this.hasLoaded
            },

            // only the question columns get an html header; `user` stays a plain label
            questionHeaders() {
                return this.headers.filter(header => header.html !== undefined)
            }
        },

        created() {
            this.listSurveyresults()
        },

        methods: {

            // $t() returns the key itself when a translation is missing, so the codebase's
            // `$t('X') || 'fallback'` idiom never fires - that's why the page rendered the
            // literal string "LoadingResults". $te() actually tests for existence.
            tr(key, ar, en) {
                if (this.$te(key)) return this.$t(key)
                return this.$i18n && this.$i18n.locale === 'en' ? en : ar
            },

            // Prefers the message the API sent over the generic catch-all, so a real
            // server-side reason is not swallowed.
            requestErrorMessage(error) {
                const payload = error && error.response ? error.response.data : null

                if (payload && payload.message) {
                    return payload.message
                }

                if (error && error.response && (error.response.status === 401 || error.response.status === 403)) {
                    return this.tr('Unauthorized',
                        'ليس لديك صلاحية لعرض نتائج هذه المشاركة.',
                        'You are not allowed to view the results of this participation.')
                }

                return this.$t('ErrorOccurred')
            },

            /**
             * Builds the grid from the API payload.
             *
             * The endpoint returns each answer as { questionId, answer, isPredefined };
             * this used to read `item.items[j].result`, a property that does not exist in
             * the response, so the very first answer threw a TypeError.
             *
             * Headers and rows are assembled in locals and assigned in one go, so a
             * payload this code cannot read leaves the previous state intact instead of
             * a half-built table.
             */
            applyResults(data) {
                const questions = Array.isArray(data.questions) ? data.questions : []
                const participants = Array.isArray(data.surveyParticipants) ? data.surveyParticipants : []

                // `text` stays plain so sorting, the column menu and any export keep working
                // and never show markup; `html` is what the header slot renders
                const headers = questions.map(x => ({
                    text: useStripHtml(x.name) || '',
                    html: useSanitizeHtml(x.name),
                    value: `q${x.id}`
                }))
                headers.splice(0, 0, { text: this.$t('Username'), value: 'user' })

                const results = []
                for (var i = 0; i < participants.length; i++) {
                    const obj = {}
                    let item = participants[i]
                    const answers = Array.isArray(item.items) ? item.items : []
                    for (var j = 0; j < answers.length; j++) {
                        let subItem = answers[j]
                        if (!subItem) {
                            continue
                        }
                        let ref = `q${subItem.questionId}`
                        obj[ref] = subItem.answer
                    }
                    obj.user = `${item.fullName || item.sourceIp || ''}`
                    results.push(obj)
                }

                this.title = data.title
                this.totalCount = data.totalCount || 0
                this.headers = headers
                this.results = results
            },

            listSurveyresults(options) {

                if (this.$route.params.id) {
                    this.loading = true
                    const page = options != undefined || options == false ? options.page : 1
                    const pageSize = options != undefined ? options.itemsPerPage : 10
                    SurveyService.listSurveyMyResults(this.$route.params.id, page, pageSize).then(res => {
                        if (res.data) {
                            try {
                                this.applyResults(res.data)
                            }
                            catch (mappingError) {
                                // A fault here means the response did not have the shape this
                                // page expects - log the payload so the mismatch is diagnosable
                                // instead of surfacing as an unexplained "an error occurred".
                                console.error('[MySurveyResults] could not read the results payload', mappingError, res.data)
                                this.$root.$notification(this.tr('ResultsCouldNotBeDisplayed',
                                    'تعذّر عرض النتائج بسبب اختلاف في تنسيق البيانات المستلمة.',
                                    'The results could not be displayed because the data returned was not in the expected format.'), 'error')
                            }
                        }
                    }).catch((error) => {
                        console.error('[MySurveyResults] failed to load survey results', error)
                        this.$root.$notification(this.requestErrorMessage(error), 'error')
                    }).finally(() => {
                        this.loading = false
                        this.hasLoaded = true
                    })
                }
                else {
                    // nothing to request - go straight to the resolved state
                    this.hasLoaded = true
                }
            }
        },
    }
</script>

<!--
    No scoped block: every element that needs styling here (the grid, its header row,
    its pagination footer) is rendered by GridWithServerPaging / v-data-table, so a
    scoped selector never matches it - the old `tbody tr:nth-of-type(2n)` striping rule
    that used to live here never applied for exactly that reason. Everything below is
    namespaced under .my-results instead, which keeps it off every other page.
-->
<style>
    /* Namespaced under .my-results so nothing leaks into other pages. */

    .my-results {
        --my-results-border: #e4e9ee;
        --my-results-text: #2b3a42;
        --my-results-muted: #78899a;
        /* Muted teal-green accent, matching the e-library and organizational-structure
           pages. It replaces --v-primary-lighten5, which resolved to a cyan. */
        --my-results-tint: #84a6a5;
        /* the green of .card-title, kept so the page stays on the system's palette */
        --my-results-green: #1b8354;
        /* teal wash derived from the primary (#0a716e) for header rows and hovers */
        --my-results-wash: #f2f7f6;
        --my-results-wash-line: #d9e7e4;
        --my-results-stripe: #fafbfb;
        --my-results-hover: #eaf3f1;
        --my-results-row-line: #eef1f4;
    }

    .my-results .my-results__card {
        border-radius: 10px;
        overflow: hidden;
    }

    /* ---------- page header bar ---------- */
    .my-results .my-results__title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 13px 18px;
        /* same green, given a little depth so it reads as a bar rather than a block */
        background: linear-gradient(180deg, #1f9260 0%, var(--my-results-green) 100%);
        border-bottom: 0;
        box-shadow: inset 0 -3px 0 rgba(0, 0, 0, .10);
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 18px;
        line-height: 1.4;
        letter-spacing: .2px;
    }

    .my-results .my-results__title-main {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .my-results .my-results__title-icon {
        color: rgba(255, 255, 255, .85) !important;
        flex: 0 0 auto;
    }

    .my-results .my-results__title-text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* the back button as a glass chip on the green, instead of a second solid colour */
    .my-results .my-results__back.v-btn {
        background: rgba(255, 255, 255, .16) !important;
        border: 1px solid rgba(255, 255, 255, .38);
        box-shadow: none !important;
        transition: background-color .18s ease, transform .18s ease;
    }

    .my-results .my-results__back.v-btn:hover {
        background: rgba(255, 255, 255, .30) !important;
        transform: translateY(-1px);
    }

    .my-results .my-results__back.v-btn .v-icon {
        color: #fff !important;
    }

    /* reserves the vertical space the grid will take, so swapping placeholder ->
       results -> empty state never shifts the page around it */
    .my-results .my-results__body {
        min-height: 460px;
    }

    /* ---------- survey identity strip ---------- */
    .my-results .my-results__toolbar {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
        padding: 13px 16px;
        background: linear-gradient(180deg, #f8fbfa 0%, var(--my-results-wash) 100%);
        border: 1px solid var(--my-results-wash-line);
        border-radius: 10px;
    }

    .my-results .my-results__toolbar-accent {
        flex: 0 0 auto;
        width: 4px;
        height: 22px;
        border-radius: 4px;
        background: var(--v-primary-base);
    }

    .my-results .my-results__heading {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 18px;
        font-weight: 600;
        color: var(--my-results-text);
        line-height: 1.4;
        margin: 0;
        /* grows to push the count chip to the far edge, shrinks before it wraps */
        flex: 1 1 auto;
        min-width: 0;
        word-break: normal;
        overflow-wrap: break-word;
        /* a title mixing Arabic and latin/numeric parts keeps its own base direction */
        unicode-bidi: plaintext;
    }

    .my-results .my-results__count {
        flex: 0 0 auto;
        font-size: 13px;
        font-weight: 600;
        color: var(--v-primary-base);
        background: #fff;
        border: 1px solid var(--my-results-wash-line);
        border-radius: 999px;
        padding: 4px 12px;
        white-space: nowrap;
    }

    /* Question headers are editor markup, so they arrive wrapped in block elements that
       would otherwise stack and stretch the header row. */
    .my-results .my-results__q p {
        display: inline;
        margin: 0;
    }

    /* `word-break: break-word` used to be here. A results table carries one column per
       question, so the columns get squeezed, and that rule then breaks *inside* words —
       with an Arabic header in a near-zero-width column it stacked one letter per line.
       `word-break: normal` never splits a word; `overflow-wrap: break-word` still rescues
       a single token too long for the column (a URL, a long English word). The min-width
       below is what actually stops the squeeze. */
    .my-results .my-results__q {
        white-space: normal;
        word-break: normal;
        overflow-wrap: break-word;
        min-width: 130px;
        max-width: 320px;
        line-height: 1.5;
        /* A question can be a whole paragraph. Three lines is the ceiling so one long
           question cannot push the header row down the screen; the `title` attribute on
           the same span shows the rest on hover. */
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    /* ---------- the grid ---------- */
    .my-results .my-results__table.v-data-table {
        background: #fff;
        border: 1px solid var(--my-results-border);
        border-radius: 10px;
        overflow: hidden;
    }

    /* Columns keep a readable floor instead of collapsing; the table then grows past the
       card and v-data-table's own wrapper scrolls it horizontally. */
    .my-results .my-results__table th,
    .my-results .my-results__table td {
        min-width: 150px;
        white-space: normal;
        word-break: normal;
        overflow-wrap: break-word;
        /* `start` rather than left/right, so one rule serves both directions */
        text-align: start;
        /* every cell renders in the direction of its own content: an Arabic answer stays
           RTL, an English or numeric one stays LTR, in the same column */
        unicode-bidi: plaintext;
    }

    .my-results .my-results__table th:first-child,
    .my-results .my-results__table td:first-child {
        min-width: 130px;
    }

    /* header row */
    .my-results .my-results__table th {
        padding: 14px 16px !important;
        height: auto !important;
        vertical-align: top;
        background: var(--my-results-wash);
        color: var(--v-primary-base) !important;
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 14px !important;
        font-weight: 600 !important;
        line-height: 1.5;
        letter-spacing: 0;
        border-bottom: 2px solid var(--my-results-wash-line) !important;
    }

    .my-results .my-results__table th + th {
        border-inline-start: 1px solid #e6efec;
    }

    /* body rows */
    .my-results .my-results__table td {
        padding: 13px 16px !important;
        height: auto !important;
        font-size: 14px;
        line-height: 1.6;
        color: var(--my-results-text);
        border-bottom: 1px solid var(--my-results-row-line) !important;
        /* digits line up column-wise instead of drifting */
        font-variant-numeric: tabular-nums;
    }

    .my-results .my-results__table tbody > tr:last-child > td {
        border-bottom: 0 !important;
    }

    /* striping first, hover last - the hover tint has to win over both parities */
    .my-results .my-results__table tbody > tr > td:first-child {
        background: #fff;
    }

    .my-results .my-results__table tbody > tr:nth-child(even) > td {
        background: var(--my-results-stripe);
    }

    .my-results .my-results__table tbody > tr:hover > td {
        background: var(--my-results-hover) !important;
    }

    .my-results .my-results__table tbody > tr {
        transition: background-color .15s ease;
    }

    /* Vuetify's own hover shade would fight the tint above */
    .my-results .my-results__table.theme--light.v-data-table
    tbody > tr:hover:not(.v-data-table__expanded__content) {
        background: transparent;
    }

    /* The name column stays put while the question columns scroll under it, so a row can
       always be traced back to its participant. */
    .my-results .my-results__table th:first-child,
    .my-results .my-results__table td:first-child {
        position: sticky;
        z-index: 2;
    }

    /* A cell taken out of flow by `position: sticky` loses its collapsed border in
       Chrome, so the edge shadow carries the row line as a second, inset shadow. */
    .v-application--is-rtl .my-results .my-results__table th:first-child {
        right: 0;
        box-shadow: -6px 0 8px -8px rgba(43, 58, 66, .35), inset 0 -2px 0 var(--my-results-wash-line);
    }

    .v-application--is-rtl .my-results .my-results__table td:first-child {
        right: 0;
        box-shadow: -6px 0 8px -8px rgba(43, 58, 66, .35), inset 0 -1px 0 var(--my-results-row-line);
    }

    .v-application--is-ltr .my-results .my-results__table th:first-child {
        left: 0;
        box-shadow: 6px 0 8px -8px rgba(43, 58, 66, .35), inset 0 -2px 0 var(--my-results-wash-line);
    }

    .v-application--is-ltr .my-results .my-results__table td:first-child {
        left: 0;
        box-shadow: 6px 0 8px -8px rgba(43, 58, 66, .35), inset 0 -1px 0 var(--my-results-row-line);
    }

    .v-application--is-rtl .my-results .my-results__table tbody > tr:last-child > td:first-child {
        box-shadow: -6px 0 8px -8px rgba(43, 58, 66, .35);
    }

    .v-application--is-ltr .my-results .my-results__table tbody > tr:last-child > td:first-child {
        box-shadow: 6px 0 8px -8px rgba(43, 58, 66, .35);
    }

    .my-results .my-results__table .v-data-table__wrapper {
        overflow-x: auto;
        /* Firefox */
        scrollbar-width: thin;
        scrollbar-color: #b0c9c3 #eef2f1;
    }

    /* the default track rendered as a heavy black bar under the last row */
    .my-results .my-results__table .v-data-table__wrapper::-webkit-scrollbar {
        height: 10px;
    }

    .my-results .my-results__table .v-data-table__wrapper::-webkit-scrollbar-track {
        background: #eef2f1;
        border-radius: 8px;
    }

    .my-results .my-results__table .v-data-table__wrapper::-webkit-scrollbar-thumb {
        background: #b0c9c3;
        border-radius: 8px;
    }

    .my-results .my-results__table .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
        background: #8fb3ac;
    }

    /* ---------- pagination footer ---------- */
    .my-results .my-results__table .v-data-footer {
        flex-wrap: wrap;
        row-gap: 8px;
        padding: 10px 12px;
        margin: 0;
        background: #fbfcfc;
        border-top: 1px solid var(--my-results-border);
        font-size: 13px;
        color: var(--my-results-muted);
    }

    .my-results .my-results__table .v-data-footer__select {
        margin-inline-end: 20px;
        font-size: 13px;
    }

    /* the page-size select as a bordered pill rather than a bare underline */
    .my-results .my-results__table .v-data-footer__select .v-select {
        margin: 0;
        margin-inline-start: 10px;
        padding: 0 6px 0 10px;
        min-height: 32px;
        background: #fff;
        border: 1px solid var(--my-results-border);
        border-radius: 8px;
        transition: border-color .18s ease;
    }

    .my-results .my-results__table .v-data-footer__select .v-select:hover {
        border-color: var(--my-results-wash-line);
    }

    .my-results .my-results__table .v-data-footer__select .v-input__slot::before,
    .my-results .my-results__table .v-data-footer__select .v-input__slot::after {
        display: none !important;
    }

    .my-results .my-results__table .v-data-footer__select .v-select__selection {
        color: var(--my-results-text);
        font-weight: 600;
    }

    .my-results .my-results__table .v-data-footer__pagination {
        margin: 0 16px;
        color: var(--my-results-text);
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }

    .my-results .my-results__table .v-data-footer__icons-before .v-btn,
    .my-results .my-results__table .v-data-footer__icons-after .v-btn {
        width: 32px;
        height: 32px;
        margin: 0 3px;
        background: #fff;
        border: 1px solid var(--my-results-border);
        border-radius: 8px;
        transition: background-color .18s ease, border-color .18s ease;
    }

    .my-results .my-results__table .v-data-footer__icons-before .v-btn:not(.v-btn--disabled):hover,
    .my-results .my-results__table .v-data-footer__icons-after .v-btn:not(.v-btn--disabled):hover {
        background: var(--my-results-wash);
        border-color: var(--my-results-wash-line);
    }

    .my-results .my-results__table .v-data-footer__icons-before .v-btn .v-icon,
    .my-results .my-results__table .v-data-footer__icons-after .v-btn .v-icon {
        color: var(--v-primary-base);
        font-size: 20px;
    }

    .my-results .my-results__table .v-data-footer .v-btn--disabled {
        background: #f5f6f7;
        opacity: .55;
    }

    /* ---------- empty state ---------- */
    .my-results .my-results__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        min-height: 420px;
        padding: 32px 24px;
    }

    .my-results .my-results__empty-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 84px;
        height: 84px;
        border-radius: 50%;
        background: var(--my-results-tint);
        margin-bottom: 18px;
    }

    .my-results .my-results__empty-icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .my-results .my-results__empty-title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 17px;
        font-weight: 600;
        color: var(--my-results-text);
        margin: 0 0 8px;
    }

    .my-results .my-results__empty-hint {
        font-size: 14px;
        color: var(--my-results-muted);
        max-width: 380px;
        margin: 0;
        line-height: 1.6;
    }

    /* ---------- responsive ---------- */
    @media (max-width: 959px) {
        .my-results .my-results__title {
            padding: 12px 14px;
            font-size: 16px;
        }

        .my-results .my-results__table th,
        .my-results .my-results__table td {
            min-width: 140px;
            padding-inline: 12px !important;
        }

        .my-results .my-results__q {
            min-width: 120px;
            max-width: 260px;
        }
    }

    @media (max-width: 599px) {
        .my-results .my-results__body {
            min-height: 360px;
            /* the grid gets the width back that the card padding was taking */
            padding-inline: 8px;
        }

        .my-results .my-results__empty {
            min-height: 320px;
            padding: 24px 16px;
        }

        .my-results .my-results__toolbar {
            padding: 12px;
            gap: 10px;
        }

        .my-results .my-results__heading {
            font-size: 16px;
            /* the count chip drops to its own line rather than squeezing the title */
            flex: 1 1 100%;
        }

        .my-results .my-results__table th,
        .my-results .my-results__table td {
            min-width: 128px;
            padding: 11px 12px !important;
            font-size: 13px !important;
        }

        .my-results .my-results__table th:first-child,
        .my-results .my-results__table td:first-child {
            min-width: 110px;
        }

        .my-results .my-results__q {
            min-width: 108px;
            max-width: 200px;
        }

        /* footer stops overflowing: the two groups centre on one wrapped row */
        .my-results .my-results__table .v-data-footer {
            justify-content: center;
            padding: 10px 8px;
        }

        .my-results .my-results__table .v-data-footer__select {
            margin-inline-end: 12px;
        }

        .my-results .my-results__table .v-data-footer__pagination {
            margin: 0 10px;
        }
    }
</style>
